import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IMeta } from 'shared/common/repositories/mortgage-broker-calculator/entities/get_offers_listing_for_newbuiling/Meta';
import { IMetaV2 } from 'shared/common/repositories/mortgage-broker-calculator/entities/get_offers_listing_for_newbuiling/MetaV2';
import { IGetOffersV2Response } from '../../common/repositories/mortgage-broker-calculator/entities/get_offers_v2/GetOffersV2Response';
import { IGetOffersV3Response } from '../../common/repositories/mortgage-broker-calculator/entities/get_offers_v3/GetOffersV3Response';
import { mortgageMiniCalculatorApi } from '../api';
import { setFinancePlatformEnabled, setIsOffersFetching, setProgramsVersion, setRedirectUrl } from '../redux';
import {
  selectEntityId,
  selectFormDefaults,
  selectRealEstateType,
  selectRegionId,
  selectSkipOffersFetch,
} from '../selectors/calculator';
import { TBenefitProgram, TObjectType, TRealEstateType } from '../types';

interface IParams {
  id: number;
  regionId?: number;
  objectType?: TObjectType | string;
  realEstate?: { id?: number; type?: TRealEstateType };
  type: string;
  objectCost: number;
  downPayment: number;
  loanTermYears: number;
  benefitProgram?: TBenefitProgram;
}

export const hasProgramsVersion = (meta: IMetaV2 | IMeta): meta is IMetaV2 => {
  return 'programsVersion' in meta;
};

export type IFetchOffersProps = Omit<IParams, 'id' | 'type' | 'objectType' | 'realEstate' | 'regionId'>;

export const useGetOffers = () => {
  const dispatch = useDispatch();
  const { objectCost, downPayment, loanTermYears, benefitProgram, objectType } = useSelector(selectFormDefaults);
  const entityId = useSelector(selectEntityId);
  const regionId = useSelector(selectRegionId);
  const realEstateType = useSelector(selectRealEstateType);
  const skipOffersFetch = useSelector(selectSkipOffersFetch);

  // Храним в state только динамические параметры, которые могут меняться через fetchOffers
  const [dynamicParams, setDynamicParams] = useState({
    objectCost,
    downPayment,
    loanTermYears,
    benefitProgram,
  });

  const defaultRealEstateType: TRealEstateType = 'newBuilding';
  const defaultObjectType: TObjectType = 'flat';

  // Мемоизируем объект realEstate (значения по умолчанию задаются при создании контекста, но для типов гарантируем не-undefined)
  const realEstate = useMemo(
    () => ({ id: entityId, type: realEstateType ?? defaultRealEstateType }),
    [realEstateType, entityId],
  );

  // Мемоизируем финальный объект параметров (objectType и realEstate всегда заданы для совместимости с v2/v3 API)
  const params = useMemo(
    () => ({
      regionId,
      objectType: objectType ?? defaultObjectType,
      ...dynamicParams,
      realEstate,
    }),
    [regionId, objectType, dynamicParams, realEstate],
  );

  const { data, isFetching, isError, error, refetch } = mortgageMiniCalculatorApi.endpoints.getOffers.useQuery(params, {
    skip: skipOffersFetch,
  });

  // Мемоизируем функцию fetchOffers для стабильности
  const fetchOffers = useCallback((newParams: IFetchOffersProps) => {
    setDynamicParams(prevParams => ({ ...prevParams, ...newParams }));
  }, []);

  /* Пока выдача пересчитывается, в сторе лежат новые параметры расчёта и старая версия программ:
     сохранять выдачу в этот момент нельзя */
  useEffect(() => {
    dispatch(setIsOffersFetching(isFetching));
  }, [dispatch, isFetching]);

  // Обработка данных только когда data изменяется
  // Redux сам проверяет изменения и не вызывает лишних ререндеров
  useEffect(() => {
    if (data) {
      const { generalLink, financePlatformEnabled } = data.meta;

      dispatch(setRedirectUrl({ url: generalLink }));
      dispatch(setFinancePlatformEnabled(financePlatformEnabled));

      if (hasProgramsVersion(data.meta)) {
        dispatch(setProgramsVersion(data.meta.programsVersion));
      }
    }
  }, [data, dispatch]);

  return {
    data: isError ? null : (data as IGetOffersV2Response | IGetOffersV3Response),
    isFetching,
    error: isError ? error : null,
    refetch,
    fetchOffers,
  };
};
